// A mastermind's members, group by group (docs/mastermind-admin-plan.md 3.2, and the critique's build plan of 20 Oct,
// P3): the group tabs (the last pick remembered per product), the group's checklist for the 7 days after its session
// (on Everyone, 1 for each group with reviews still due), Find a member (every group) and the filters, the legend of
// the levels, the table at laptop width and 1 card each on a phone, worst first, the members who've left, and
// Guarantee rules. 1 read for the members (hub.mastermind_members), with the product's groups, sessions, the sessions
// to come and its recordings, and the groups' WhatsApp links beside it.
import { useEffect, useMemo, useState } from 'react'
import { Link, navigate } from '../../lib/router.jsx'
import { Button, Card, Chip, Chips, EmptyState, Loading, PageHeader, Problem, SearchBox, Table, Tabs, adminPath, useAdmin, useLoad } from '../ui/index.js'
import { time } from '../../lib/format.js'
import { MarkIcon } from '../../products/mastermind/Log.jsx'
import { isBehind, levelBands } from '../../products/mastermind/levels.js'
import { dm, wdm } from '../../products/mastermind/log.js'
import { MM } from './words.js'
import { loadGroups, loadLinks, loadMembers, loadNextSessions, loadRecordings, loadRules, loadSessions } from './data.js'
import { afterSession, afterSessions, defaultGroup, groupTabs, memberRow, pickRows } from './section.js'
import { Rules } from './Rules.jsx'

const remembered = productId => { try { return window.localStorage.getItem(`mm-group:${productId}`) } catch { return null } }
const remember = (productId, key) => { try { window.localStorage.setItem(`mm-group:${productId}`, key) } catch { /* the page works without it */ } }
const SHOWS = ['needs', 'off', 'money']

// The mark beside where they stand: a filled dot, a hollow ring (bold words) for any level of behind, or an outline dash
export function StandMark({ state, children }) {
  const mark = state === 'on_track' ? 'on' : isBehind(state) || state === 'off_track' ? 'off' : 'todo'
  return <span className={`amx-stand amx-stand--${mark}`}><MarkIcon mark={mark} />{children}</span>
}

// 1 line of the checklist: done (a filled tick) or still to do (an empty ring), its words and the way to do it
function CheckLine({ done, text, children }) {
  return (
    <li className={`amx-check-line${done ? ' amx-check-line--done' : ''}`}>
      <MarkIcon mark={done ? 'done' : 'due'} />
      <span className="amx-check-text"><span className="sr-only">{done ? MM.checkDone : MM.checkToDo}: </span>{text}</span>
      {children}
    </li>
  )
}

// After a group's session: its checklist for the 7 days after (the critique's admin 7), each line ticking itself off
function AfterSession({ group, name, after, link, open }) {
  const heading = after.left ? MM.met(name, after.day, after.left, after.total) : MM.metDone(name, after.day)
  const next = after.next
  return (
    <section className="amx-after" aria-label={MM.checklist(name)}>
      <p className="amx-after-line">{heading}</p>
      <ul className="amx-check">
        <CheckLine done={!after.register} text={MM.checkRegister(after.marked, after.total)}>
          {after.register && <Link className="ad-btn ad-btn--line ad-btn--sm" to={adminPath.register(after.session)}>{MM.takeRegister}</Link>}
        </CheckLine>
        {after.recording !== null && (
          <CheckLine done={after.recording} text={MM.checkRecording(after.recording)}>
            {!after.recording && <Link className="ad-btn ad-btn--line ad-btn--sm" to={adminPath.recordings()}>{MM.addRecording}</Link>}
          </CheckLine>
        )}
        {next !== undefined && (
          <CheckLine done={Boolean(next?.zoom_join_url)} text={next ? MM.checkNext(wdm(next.starts_at), time(next.starts_at), Boolean(next.zoom_join_url)) : MM.checkNoNext}>
            {!next?.zoom_join_url && <Link className="ad-btn ad-btn--line ad-btn--sm" to={`${adminPath.dates()}#cohort-${group}`}>{MM.setIt}</Link>}
          </CheckLine>
        )}
        <CheckLine done={!after.left} text={MM.checkReviews(after.left, after.total)}>
          {after.first && <Link className="ad-btn ad-btn--ink ad-btn--sm" to={open(after.first, { show: 'review' }, group)}>{MM.reviewStart(after.first.name)}</Link>}
        </CheckLine>
      </ul>
      <Link className="amx-after-link" to={adminPath.dates()}>{link?.whatsapp_invite_url ? MM.whatsappYes : MM.whatsappNo}</Link>
    </section>
  )
}

export function Members({ product, role, query }) {
  const { api, personId: me } = useAdmin()
  const nowMs = Date.now()
  const page = useLoad(async () => {
    const [members, groups, sessions, links, next, recordings, rules] = await Promise.all([
      loadMembers(api, product.id), loadGroups(api, product.id), loadSessions(api, product.id), loadLinks(api),
      loadNextSessions(api, product.id), loadRecordings(api, product.id), loadRules(api, product.id),
    ])
    return { members: members || [], groups: groups || [], sessions: sessions || [], links: links || [], next, recordings, rules }
  }, [api, product.id])
  const [left, setLeft] = useState({ open: query.get('left') === '1', rows: null, error: null })
  const [search, setSearch] = useState('')

  // a level set by hand says who set it: "On track, set by you" (migration 80)
  const rows = useMemo(() => (page.data?.members || []).map(m => memberRow(m, { me })), [page.data, me])
  const groups = page.data?.groups || []
  const fromQuery = query.get('group')
  const [group, setGroup] = useState(null)
  useEffect(() => {
    if (!page.data) return
    const keys = [...groups.map(g => g.id), 'none', 'all']
    const pick = [fromQuery, remembered(product.id)].find(k => k && keys.includes(k)) || defaultGroup(groups, page.data.sessions, nowMs)
    setGroup(g => g || pick)
  }, [page.data]) // eslint-disable-line react-hooks/exhaustive-deps
  const show = SHOWS.includes(query.get('show')) ? query.get('show') : 'all'

  useEffect(() => {
    if (!left.open || left.rows) return
    loadMembers(api, product.id, { left: true }).then(r => setLeft(s => ({ ...s, rows: (r || []).map(m => ({ ...memberRow(m, { me }), status: m.access_status })) })))
      .catch(e => setLeft(s => ({ ...s, error: e })))
  }, [left.open]) // eslint-disable-line react-hooks/exhaustive-deps

  const head = <PageHeader eyebrow={MM.eyebrow} title={product.name} lede={MM.lede} />
  if (page.status === 'loading' || !group) return <>{head}<Loading /></>
  if (!page.data) return <>{head}<Problem error={page.error} onRetry={page.reload} /></>

  const setQuery = changes => {
    const q = new URLSearchParams(query)
    for (const [k, v] of Object.entries(changes)) { if (v == null || v === 'all' && k === 'show') q.delete(k); else q.set(k, v) }
    const s = q.toString()
    navigate(`${adminPath.mastermind(product.slug)}${s ? `?${s}` : ''}`, { replace: true, scroll: false })
  }
  const pickGroup = key => { setGroup(key); remember(product.id, key); setQuery({ group: key }) }
  const tabs = groupTabs(rows, groups)
  const inGroup = pickRows(rows, { group, show: 'all' })
  const searching = Boolean(search.trim())
  const shown = pickRows(rows, { group, show, search })
  const groupName = groups.find(g => g.id === group)?.name
  const extra = { recordings: page.data.recordings, next: page.data.next }
  const afters = group === 'all'
    ? afterSessions(groups, page.data.sessions, rows, nowMs, extra)
    : (() => { const a = afterSession(group, page.data.sessions, rows, nowMs, extra); return a ? [{ group, name: groupName, after: a }] : [] })()
  const memberQuery = (more, inGroupId = group) => {
    const q = new URLSearchParams()
    q.set('group', inGroupId)
    if (show !== 'all') q.set('show', show)
    for (const [k, v] of Object.entries(more || {})) q.set(k, v)
    return `?${q}`
  }
  // a match a search found in another group opens in its own group, so Previous and Next step through the members it's
  // with (Everyone's matches stay in Everyone)
  const groupOf = r => (searching && group !== 'all' ? r.cohortId || 'none' : group)
  const open = (r, more, inGroupId = groupOf(r)) => `${adminPath.mmMember(product.slug, r.id)}${memberQuery(more, inGroupId)}`
  // the nudge: the log's tab with Send open, under Before you send it
  const sendHref = r => `${adminPath.mmLog(product.slug, r.id)}${memberQuery({ send: '1' }, groupOf(r))}`
  const needsCount = inGroup.filter(r => r.needs).length
  const offCount = inGroup.filter(r => r.off).length
  const moneyCount = inGroup.filter(r => r.money).length
  const sentLine = r => (r.logKnown ? (r.lastSent ? MM.logSent(dm(r.lastSent)) : MM.logNotSent) : null)

  const columns = [
    { key: 'member', label: MM.cols.member, primary: true, width: 'minmax(0, 1.5fr)', render: r => (
      <span className="amx-member">
        <Link to={open(r)} className="amx-member-link">{r.name}</Link>
        {r.chip && <span className="ad-tag amx-chip">{r.chip}</span>}
        {searching && <span className="ad-tag amx-group-tag">{MM.inGroup(r.group)}</span>}
      </span>
    ) },
    { key: 'month', label: MM.cols.month, width: '88px' },
    { key: 'thisMonth', label: MM.cols.thisMonth, width: 'minmax(0, 0.9fr)' },
    { key: 'sessions', label: MM.cols.sessions, width: '100px' },
    // only once the summary has the log's own counts (section.js memberRow)
    ...(rows.some(r => r.actions != null) ? [{ key: 'actions', label: MM.cols.actions, width: '108px' }] : []),
    { key: 'stand', label: MM.cols.stand, width: 'minmax(0, 1.3fr)', render: r => (
      <span className="amx-stand-cell">
        <StandMark state={r.state}>{r.stand}</StandMark>
        {sentLine(r) && <span className="amx-muted">{sentLine(r)}</span>}
        {r.off && <Link className="amx-send-link" to={sendHref(r)} aria-label={MM.sendLogTo(r.name)}>{MM.sendLog}</Link>}
      </span>
    ) },
    { key: 'needs', label: MM.cols.needs, width: 'minmax(0, 1fr)', render: r => (r.needs
      ? <span className="amx-needs-cell"><span className="amx-needs">{r.needs.text}</span>{r.also && <span className="amx-also">{r.also}</span>}</span>
      : '') },
  ]

  return (
    <>
      {head}
      <div className="amx-tabs-row">
        <Tabs label={MM.groupsLabel} current={group} items={tabs.map(t => ({ key: t.key, label: t.label, onClick: () => pickGroup(t.key) }))} />
      </div>

      {afters.map(a => (
        <AfterSession key={a.group} group={a.group} name={a.name} after={a.after} open={open}
          link={page.data.links.find(l => l.cohort_id === a.group)} />
      ))}

      <div className="amx-tools">
        <SearchBox value={search} onChange={setSearch} label={MM.find} placeholder={MM.find} />
        <Chips label={MM.show}>
          <Chip pressed={show === 'all'} onClick={() => setQuery({ show: 'all' })}>{MM.all}</Chip>
          <Chip pressed={show === 'needs'} onClick={() => setQuery({ show: 'needs' })}>{MM.needs(needsCount)}</Chip>
          <Chip pressed={show === 'off'} onClick={() => setQuery({ show: 'off' })}>{MM.off(offCount)}</Chip>
          <Chip pressed={show === 'money'} onClick={() => setQuery({ show: 'money' })}>{MM.money(moneyCount)}</Chip>
        </Chips>
      </div>
      <p className="amx-legend">{MM.legend(levelBands())} {MM.slipIs}</p>

      {!inGroup.length && !searching
        ? <Card><EmptyState>{group === 'all' || group === 'none' ? MM.noMatch : MM.emptyGroup(groupName)}</EmptyState></Card>
        : (
          <>
            <Card body={false} className="amx-table-card">
              <Table label={product.name} columns={columns} rows={shown} rowKey={r => r.id} onRow={r => navigate(open(r))} empty={MM.noMatch} />
            </Card>
            <ul className="amx-cards" aria-label={product.name}>
              {shown.map(r => (
                <li key={r.id} className="amx-card">
                  <Link to={open(r)} className="amx-card-link">
                    <span className="amx-card-top">
                      <span className="amx-card-name">{r.name}</span>
                      {r.chip && <span className="ad-tag amx-chip">{r.chip}</span>}
                      {searching && <span className="ad-tag amx-group-tag">{MM.inGroup(r.group)}</span>}
                    </span>
                    <StandMark state={r.state}>{r.stand}</StandMark>
                    {sentLine(r) && <span className="amx-muted">{sentLine(r)}</span>}
                    <span className="amx-card-line">{MM.phoneLine({ month: r.month, thisMonth: r.thisMonth, sessions: r.sessions, actions: r.actions })}</span>
                    {r.needs && <span className="amx-card-needs">{MM.phoneNeeds(r.needs.text)}</span>}
                    {r.also && <span className="amx-also">{r.also}</span>}
                  </Link>
                  {r.off && <Link className="amx-send-link amx-card-send" to={sendHref(r)} aria-label={MM.sendLogTo(r.name)}>{MM.sendLog}</Link>}
                </li>
              ))}
              {!shown.length && <li className="amx-card"><EmptyState>{MM.noMatch}</EmptyState></li>}
            </ul>
          </>
        )}

      <section className="amx-left" aria-labelledby="amx-left-h">
        <div className="amx-left-head">
          <h2 className="ad-card-title" id="amx-left-h">{left.rows ? MM.left(left.rows.length) : MM.leftH}</h2>
          <Button size="sm" aria-expanded={left.open} onClick={() => { setLeft(s => ({ ...s, open: !s.open })); setQuery({ left: left.open ? null : '1' }) }}>
            {left.open ? MM.leftHide : MM.leftShow}
          </Button>
        </div>
        {left.open && (left.error
          ? <Problem error={left.error} />
          : !left.rows ? <Loading /> : !left.rows.length ? <EmptyState>{MM.leftNone}</EmptyState> : (
            <ul className="amx-left-list">
              {left.rows.sort((a, b) => a.name.localeCompare(b.name, 'en-GB')).map(r => (
                <li key={r.id} className="amx-left-row">
                  <Link to={adminPath.mmLog(product.slug, r.id)}>{r.name}</Link>
                  <span className="ad-tag">{MM.lastStatus(r.status)}</span>
                  <span className="amx-muted">{r.stand}</span>
                </li>
              ))}
            </ul>
          ))}
      </section>

      <Rules product={product} stored={page.data.rules} role={role} members={page.data.members} />
    </>
  )
}
